import { ref, onMounted } from 'vue'

export default function useSecurityChecks() {
  const fingerprint = ref('')
  const pageLoadTime = ref(Date.now())
  const formInteractionCount = ref(0)
  const mouseMovements = ref(0)
  const lastFieldValue = ref('')
  const typingSpeed = ref([])
  const formStartTime = ref(0)
  const isChecking = ref(false)
  const toast = useToast()

  // Generate a simple browser fingerprint
  function generateFingerprint() {
    // Only generate fingerprint on client-side to prevent hydration mismatch
    if (!import.meta.client || typeof window === 'undefined') {
      return 'ssr-placeholder'
    }

    const nav = navigator
    const screen = window.screen

    // Combine various browser properties for a simple fingerprint
    const components = [
      nav.userAgent,
      screen.colorDepth,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      nav.language || '',
      nav.hardwareConcurrency || '',
      (nav as any).deviceMemory || '',
      nav.platform || '',
      'plugins:' + (nav.plugins ? nav.plugins.length : ''),
    ]

    // Create a hash from these components
    return hashString(components.join('|'))
  }

  // Simple hash function
  function hashString(str) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }
    return hash.toString(36)
  }

  // Detect whether we're running in a bot environment
  function detectAutomation() {
    // Various bot detection signals
    const signals = {
      webdriver: !!navigator.webdriver,
      automationControlled: !!(
        navigator.webdriver ||
        window.document.documentElement.getAttribute('webdriver')
      ),
      isHeadless: !!(
        navigator.userAgent.includes('Headless') ||
        navigator.languages === undefined ||
        navigator.languages.length === 0
      ),
      suspiciousTiming:
        Date.now() - pageLoadTime.value < 1000 &&
        formInteractionCount.value < 2,
      noMouseMovement: mouseMovements.value < 3,
      unnaturalTyping:
        typingSpeed.value.length > 3 &&
        typingSpeed.value.every((interval) => interval < 50 || interval > 800),
    }

    // Count number of positive bot signals
    const signalCount = Object.values(signals).filter(Boolean).length

    // Return risk score and signals
    return {
      isLikelyBot: signalCount >= 2,
      riskScore: signalCount / Object.keys(signals).length,
      signals,
    }
  }

  // Track user interactions with the form
  function trackFormInteractions(formElement) {
    if (!formElement) return

    // Track when form interaction begins
    formElement.addEventListener('focusin', () => {
      if (formStartTime.value === 0) {
        formStartTime.value = Date.now()
      }
      formInteractionCount.value++
    })

    // Track typing behavior
    const inputElements = formElement.querySelectorAll(
      'input[type="text"], input[type="email"]',
    )
    let lastTypeTime = 0

    inputElements.forEach((input) => {
      input.addEventListener('input', (e) => {
        const now = Date.now()

        // Calculate typing interval if it's not the first character
        if (lastTypeTime > 0 && lastFieldValue.value !== '') {
          const interval = now - lastTypeTime
          typingSpeed.value.push(interval)
        }

        lastTypeTime = now
        lastFieldValue.value = e.target.value
      })
    })

    // Track mouse movements
    document.addEventListener(
      'mousemove',
      () => {
        mouseMovements.value++
      },
      { passive: true },
    )
  }

  // Get security data to send with form submission
  function getSecurityData() {
    const automationCheck = detectAutomation()

    return {
      fp: fingerprint.value,
      loadToSubmit: Date.now() - pageLoadTime.value,
      interactions: formInteractionCount.value,
      mouseMovements: mouseMovements.value > 100 ? 100 : mouseMovements.value,
      formCompletionTime:
        formStartTime.value > 0 ? Date.now() - formStartTime.value : 0,
      automationScore: automationCheck.riskScore,
    }
  }

  /**
   * Check if an email is already subscribed
   * @param email The email to check
   * @returns An object with exists and status properties, or null if there was an error
   */
  const checkSubscriptionStatus = async (email: string) => {
    if (!email) {
      return { exists: false, status: null }
    }

    isChecking.value = true

    try {
      const securityData = getSecurityData()
      const response = await $fetch('/api/subscribe/check-status', {
        method: 'POST',
        body: {
          email,
          fingerprint: securityData.fp,
        },
      })

      return response
    } catch (error) {
      console.error('Error checking subscription status:', error)
      toast.add({
        title: 'Error',
        description: 'Failed to check subscription status',
        color: 'red',
      })
      return null
    } finally {
      isChecking.value = false
    }
  }

  /**
   * Process a subscription with duplicate check and error handling
   * @param email The email to subscribe
   * @param additionalPayload Additional payload data to merge with security data
   * @param onSuccess Callback function for successful subscription
   */
  const processSubscription = async (
    email: string,
    additionalPayload: Record<string, unknown>,
    onSuccess: (response: unknown) => void,
  ) => {
    if (!email) {
      toast.add({
        title: 'Error',
        description: 'Please enter your email',
        color: 'red',
      })
      return
    }

    isChecking.value = true

    try {
      // Generate security data including fingerprint first
      const securityData = getSecurityData()

      // Check if this browser or email has already subscribed
      console.log('Checking if email exists:', email)
      let statusCheck = null

      try {
        statusCheck = await $fetch('/api/subscribe/check-status', {
          method: 'GET',
          params: {
            email,
            fingerprint: securityData.fp,
          },
        })
        console.log('Email status check result:', statusCheck)
      } catch (checkError) {
        console.error('Error checking email status:', checkError)
        // Continue with subscription flow even if check fails,
        // the server will handle duplicate detection as a fallback
      }

      // Handle server-side duplicate detection
      if (statusCheck?.exists) {
        let message = 'This email is already in our system'

        // If the status indicates that this browser has already subscribed with a different email
        if (
          statusCheck.status === 'browser_subscribed' &&
          statusCheck.subscribedEmail
        ) {
          message = `You've already subscribed`
        }

        toast.add({
          title: 'Already subscribed',
          description: message,
          color: 'primary',
        })
        isChecking.value = false
        return
      }

      // Combine security data with additional payload
      const payload = {
        ...additionalPayload,
        email,
        security: securityData,
      }

      console.log('Submitting to waitlist with payload:', {
        ...payload,
        email: email ? '[REDACTED]' : undefined,
        security: '...',
      })

      const response = (await $fetch('/api/waitlist/join', {
        method: 'POST',
        body: payload,
      })) as { success: boolean; message?: string }

      console.log('Waitlist join response:', response)

      if (response.success) {
        onSuccess(response)
      } else {
        console.error('API returned success:false', response)
        toast.add({
          title: 'Error',
          description: response.message || 'Failed to join waitlist',
          color: 'red',
        })
      }
    } catch (error: any) {
      console.error('Subscription error details:', {
        message: error.message,
        data: error.data,
        status: error.status,
        code: error.statusCode,
      })

      // If it's a 409 (conflict) error, handle it as a duplicate
      if (
        error.status === 409 ||
        error.statusCode === 409 ||
        (error.data?.message && error.data.message.includes('already exists'))
      ) {
        let message = 'This email is already in our system'

        // Check if the error contains information about a previously subscribed email
        if (error.data?.subscribedEmail) {
          message = `You've already subscribed`
        }

        toast.add({
          title: 'Already subscribed',
          description: message,
          color: 'primary',
        })
      } else {
        toast.add({
          title: 'Error',
          description: error.data?.message || 'Failed to join waitlist',
          color: 'red',
        })
      }
    } finally {
      isChecking.value = false
    }
  }

  // Initialize fingerprinting and tracking
  onMounted(() => {
    fingerprint.value = generateFingerprint()
    pageLoadTime.value = Date.now()

    // Wait for the DOM to be ready before tracking form
    setTimeout(() => {
      const formElement = document.querySelector('form')
      if (formElement) {
        trackFormInteractions(formElement)
      }
    }, 100)
  })

  return {
    getSecurityData,
    isChecking,
    checkSubscriptionStatus,
    processSubscription,
    testCheckStatus: async (email) => {
      try {
        const securityData = getSecurityData()
        const result = await $fetch('/api/subscribe/check-status', {
          method: 'POST',
          body: {
            email,
            fingerprint: securityData.fp,
          },
        })
        console.log('Status check result:', result)
        return result
      } catch (error) {
        console.error('Status check error:', error)
        return null
      }
    },
  }
}
